/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import { CallDirection, CallStatus } from '../typings';
import { getCallIConsAndStyles } from '../utils/get-call-icons-and-styles';

interface CallHeaderProps {
  state: CallStatus;
  direction?: CallDirection;
  wabaLabel: string;
  wabaNumber: string;
  onClose?: () => void;
  onMinimize?: () => void;
}

const getCloseIcon = (state: CallStatus, color: string) => {
  switch (state) {
    case CallStatus.INITIATED:
      return <KeyboardArrowUpIcon sx={{ color: '#7a7a7a' }} />;
    case CallStatus.IN_PROGRESS:
    case CallStatus.Connecting:
    case CallStatus.Ringing:
      return <KeyboardArrowUpIcon sx={{ color: '#fff' }} />;
    case CallStatus.MISSED:
    case CallStatus.FAILED:
      return <CloseIcon sx={{ color: '#5f6368' }} />;
    default:
      return null;
  }
};

export const CallHeader = ({ state, direction, wabaLabel, wabaNumber, onClose, onMinimize }: CallHeaderProps) => {
  const { bg, titleColor, iconColor, icon, label, wabaLabelColor, wabaNumberColor } = getCallIConsAndStyles(
    state,
    24,
    direction || CallDirection.INCOMING
  );

  const getClickHandler = (state: CallStatus, onClose?: () => void, onMinimize?: () => void) => {
    const closeStates = [CallStatus.MISSED, CallStatus.END, CallStatus.FAILED];
    return closeStates.includes(state) ? onClose : onMinimize;
  };

  return (
    <Box
      css={css`
        background-color: ${bg};
        border-top-left-radius: 12px;
        border-top-right-radius: 12px;
      `}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" padding="16px 12px 12px 16px">
        <Box display="flex" flexDirection="column" gap="4px">
          <Box display="flex" alignItems="center" gap="6px">
            <Typography fontSize="14px" fontWeight={[CallStatus.END].includes(state) ? 600 : 500} color={titleColor}>
              {label}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap="8px">
            <Box sx={{ color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
            <Box display="flex" flexDirection="column" sx={{ color: titleColor }}>
              <Typography fontSize="13px" fontWeight={500} color={wabaLabelColor}>
                {wabaLabel}
              </Typography>
              <Typography fontSize="12px" fontWeight={500} color={wabaNumberColor}>
                {wabaNumber}
              </Typography>
            </Box>
          </Box>
        </Box>

        <IconButton
          size="small"
          sx={{ mt: '2px', p: '4px' }}
          onClick={getClickHandler(state, onClose, onMinimize)}
          disabled={state === CallStatus.END}
        >
          {getCloseIcon(state, wabaLabelColor)}
        </IconButton>
      </Box>

      {state === CallStatus.END && <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0' }} />}
    </Box>
  );
};
